<script setup lang="ts">
import { TOOLS_SCREEN_TITLE } from '@zerohuman/shared'
import { Button, Input, Spinner, ZhButton, ZhPanel, ZhSystemLabel } from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api, type OnboardingStateView, requestFailure } from '@/api'
import AssemblyLog from '@/components/AssemblyLog.vue'
import TeamSetupStep from '@/components/TeamSetupStep.vue'
import { backToSetupChat, setupChatFrom } from '@/lib/backToSetupChat'
import { onboardingSession } from '@/lib/onboardingSession'
import { SET_UP_WITH_COO } from '@/lib/setupChat'
import { connectToolLocation } from '@/lib/teamSetup'

/**
 * The guided `team_setup` step (#1739). It reads the server-held setup state on the guided path, the same state the
 * COO's chat reads, and shows its one current item: what the team cloned during setup still needs, with the reason
 * and a Connect button. The server orders the list and decides when the step is complete, so this screen keeps no
 * order and no flag of its own: it shows whatever item the server serves next.
 *
 * - Connect opens the existing Tools page on the connect flow for that tool type. There is no connect of its own.
 * - The first piece of work is typed here and saved as the step's answer; the server refuses it, in plain words, while
 *   a connection is still missing.
 * - When the team cannot start, the server's own plain-words report says what is missing.
 *
 * Its heading is the step-by-step tools screen's shared title (#1741), so the guided screen and any other surface
 * that names it say the same thing.
 *
 * It is guided screen 5 from the `assembly` step on (#1743): the assembly log of real setup events comes first, above
 * the tool rows, from the same server events the setup chat shows. While setup is still at `assembly` the tool rows
 * wait; once the log says the work has finished, the state is read again so they appear.
 *
 * Never an empty or dead-end screen (#2154):
 * - Until the first state arrives it says it is loading, with the kit's turning circle beside the words so a slow load
 *   does not look like a stuck page. A later read (the assembly finishing) keeps the step on screen.
 * - A failed read says so, with Try again, which reads the setup state again: never a whole page reload. Try again is
 *   a fresh start, so it also clears what an earlier action said; the assembly's own re-read does not.
 * - A failed save keeps what was typed. It shows the server's own words only for its 400 refusal of the step, which
 *   are written for the person; any other failure (offline, a server fault) is one fixed sentence, never raw text.
 * - On its own route it has "Set up with your COO instead" until setup is complete, so a person not ready to connect
 *   a tool can leave. Inside the guided shell (`embedded`) the shell's own control is the way out, so it draws none.
 */
const props = withDefaults(
    defineProps<{
        /** Drawn inside the guided shell, which has its own "Set up with your COO instead". */
        embedded?: boolean
    }>(),
    { embedded: false },
)

const LOADING = 'Loading your team’s setup…'
const LOAD_FAILED = 'Could not load your team’s setup.'
const SAVE_FAILED = 'Could not save your first piece of work. Try again.'
const OPEN_CHAT_FAILED = 'Could not open the setup conversation.'

const route = useRoute()
const router = useRouter()
const session = onboardingSession()
const state = ref<OnboardingStateView | null>(null)
/** A read of the setup state is on its way. */
const reading = ref(true)
/** Why the last read of the setup state failed; Try again reads it again. Kept apart from what an action says. */
const loadFailure = ref<string | null>(null)
/** Why the last thing the person did (saving the first piece of work, going back to the chat) did not happen. */
const actionFailure = ref<string | null>(null)
const firstWork = ref('')
const saving = ref(false)
const leaving = ref(false)

const teamSetup = computed(() => state.value?.teamSetup ?? null)
const current = computed(() => teamSetup.value?.current ?? null)
/** Only before a state has arrived: a later read leaves the step on screen. */
const loading = computed(() => reading.value && !state.value)
const failure = computed(() => [loadFailure.value, actionFailure.value].filter(Boolean).join(' '))
const offerBackToChat = computed(() => !props.embedded && state.value?.status !== 'complete')

/** Reads the setup state, clearing the last read's failure: on mount, on Try again, and when the assembly finishes. */
async function read(): Promise<void> {
    reading.value = true
    loadFailure.value = null
    try {
        state.value = await api.onboarding('guided', session)
    } catch {
        loadFailure.value = LOAD_FAILED
    } finally {
        reading.value = false
    }
}

onMounted(read)

/**
 * Try again: reads the setup state again and drops what an earlier action said (a way out that could not open the
 * conversation, a save that failed), so a screen that loads is not left beside a stale failure. What was typed stays.
 */
async function retry(): Promise<void> {
    actionFailure.value = null
    await read()
}

/** Opens the Tools page's connect flow for `toolType`. */
async function connect(toolType: string): Promise<void> {
    await router.push(connectToolLocation(toolType))
}

/**
 * What a failed save says. The server's 400 refusal of the step is its own plain-words sentence (#1739: what the team
 * still needs before it can start), so it is shown as it is. Anything else, a network error or a server fault, is the
 * fixed sentence: its raw text was not written for the person.
 */
function saveFailureText(error: unknown): string {
    const refused = requestFailure(error)?.status === 400 ? (error as Error).message : ''
    return refused || SAVE_FAILED
}

/** Saves the first piece of work; the server answers with the next state, or says what is still missing. */
async function saveFirstWork(): Promise<void> {
    saving.value = true
    actionFailure.value = null
    try {
        state.value = await api.saveOnboardingStep(
            'team_setup',
            { firstWork: firstWork.value.trim() },
            'guided',
            session,
        )
    } catch (error) {
        actionFailure.value = saveFailureText(error)
    } finally {
        saving.value = false
    }
}

/** "Set up with your COO instead": setup moves to the chat path and the setup conversation opens. */
async function backToChat(): Promise<void> {
    leaving.value = true
    actionFailure.value = null
    try {
        await backToSetupChat(router, setupChatFrom(route.query.chat))
    } catch {
        actionFailure.value = OPEN_CHAT_FAILED
    } finally {
        leaving.value = false
    }
}
</script>

<template>
    <section class="flex w-full flex-col gap-4 p-6" data-testid="onboarding-team-setup">
        <header class="flex flex-wrap items-start justify-between gap-4">
            <div class="flex flex-col gap-4">
                <ZhSystemLabel tone="muted">Team setup</ZhSystemLabel>
                <h1 class="text-xl font-semibold" data-testid="team-setup-title">{{ TOOLS_SCREEN_TITLE }}</h1>
            </div>
            <Button
                v-if="offerBackToChat"
                type="button"
                variant="link"
                data-testid="team-setup-back-to-chat"
                :disabled="leaving"
                @click="backToChat"
            >
                {{ SET_UP_WITH_COO }}
            </Button>
        </header>
        <Spinner v-if="loading" :label="LOADING" class="self-start text-sm" data-testid="team-setup-loading" />
        <AssemblyLog v-if="state?.assemblyId" :assembly-id="state.assemblyId" @finished="read" />
        <p v-if="teamSetup?.message" role="status" class="text-sm" data-testid="team-setup-message">
            {{ teamSetup.message }}
        </p>
        <p v-if="failure" role="alert" class="text-sm" data-testid="team-setup-failure">{{ failure }}</p>
        <div v-if="loadFailure">
            <ZhButton variant="ghost" data-testid="team-setup-retry" @click="retry">Try again</ZhButton>
        </div>
        <TeamSetupStep
            v-if="state && state.currentStep !== 'assembly'"
            :current="current"
            :remaining="teamSetup?.items.length ?? 0"
            @connect="connect"
        />
        <ZhPanel v-if="current?.kind === 'first_work'" class="space-y-3" data-testid="team-setup-first-work">
            <label class="block text-sm" for="team-setup-first-work-input">What should your team do first?</label>
            <Input id="team-setup-first-work-input" v-model="firstWork" data-testid="team-setup-first-work-input" />
            <ZhButton
                data-testid="team-setup-first-work-save"
                :disabled="firstWork.trim() === ''"
                :loading="saving"
                loading-label="SAVING..."
                @click="saveFirstWork"
            >
                Save
            </ZhButton>
        </ZhPanel>
    </section>
</template>
